/**
 * @description A form submission hook that provides a submit handler callback function and submit state
 */

import React from 'react';

import { SetSyncResultsContext } from '@seccon/shared/personnelSyncResults/contexts/SyncResults';
import decodeJson from '@seccon/shared/utils/decodeJson';
import { ToastContext } from '@seccon/core/contexts/toast';

const useSubmit = (form) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { success, error } = React.useContext(ToastContext);
  const setResults = React.useContext(SetSyncResultsContext);

  /**
   * Handles API request successes by storing the response in the results context, notifying the user, and
   *  reseting the form
   * @param response {object} - API Response
   */
  function handleSuccess (response) {
    success('Personnel sync file was processed successfully.');

    setResults(response);

    form.reset();
  }

  /**
   * Handles API request failures by notifying the user
   * @param err {{ message: string }} - API error response
   */
  function handleFailure ({ body: err }) {
    const msg = err?.message ? `Error: ${ err.message }` : 'Personnel sync file could not be processed. Try again later.';

    error(msg);
  }

  /**
   * Handles form submission by making an API request to parse the file and storing the response in the results context
   * @param data {{ syncMap: { name: string }, syncFile: File[], syncOnSsn: boolean }} - form data submitted
   */
  function handleSubmit (data) {
    // Prepare the form data
    const requestBody = new FormData();
    requestBody.append('mappingName', data.syncMap.name);
    requestBody.append('personnelFile', data.syncFile[0]);
    requestBody.append('syncOnSsn', data.syncOnSsn);

    // Set the submitting state to true and clear the form results
    setIsSubmitting(true);
    setResults(null);

    fetch('/personnelSync/parse', {
      method: 'POST',
      body: requestBody
    })
      .then(decodeJson)
      .then(handleSuccess)
      .catch(handleFailure)
      .finally(() => {
        // Regardless of success or failure, turn off the submitting state
        setIsSubmitting(false);
      });
  }

  return { isSubmitting, handleSubmit };
};

export default useSubmit;
